import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import styles from './SettingsPage.module.css';
import { ThemeContext } from '../context/ThemeContext';
import api, { getAdminSettings, updateAdminSettings, getAdminMe } from '../utils/api';

const SettingsPage = () => {
    const { theme } = useContext(ThemeContext);
    const [settings, setSettings] = useState({
        adminName: '',
        accountNumber: '',
        bankName: '',
        ifscCode: '',
        upiId: '',
        currentPayment: 'Both',
        qrCode: '',
    });
    const [initialSettings, setInitialSettings] = useState(null); // To store original settings
    const [adminId, setAdminId] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [message, setMessage] = useState('');
    const [showChangeQrButton, setShowChangeQrButton] = useState(false);
    const fileInputRef = useRef(null);

    const isDirty = initialSettings && (
        JSON.stringify(settings) !== JSON.stringify(initialSettings) ||
        selectedFile !== null
    );

    const fetchSettings = useCallback(async () => {
        try {
            // First, always try to fetch current admin info to get actual _id from token
            try {
                const adminMeRes = await getAdminMe();
                setAdminId(adminMeRes.data._id);
            } catch (err) {
                console.error("Error fetching admin details:", err);
            }

            // Then try to fetch public settings
            try {
                const { data } = await getAdminSettings();
                const fetchedSettings = {
                    adminName: data.adminName || '',
                    accountNumber: data.accountNumber || '',
                    bankName: data.bankName || '',
                    ifscCode: data.ifscCode || '',
                    upiId: data.upiId || '',
                    currentPayment: data.currentPayment || 'Both',
                    qrCode: data.qrCode || '',
                };
                setSettings(fetchedSettings);
                setInitialSettings(fetchedSettings); // Store initial settings
            } catch (error) {
                console.error("Error fetching admin settings:", error);
                if (error.response && error.response.status === 404) {
                    setMessage('Settings not found. Please check PUBLIC_BANK_DETAILS_ADMIN_ID in backend .env');
                } else {
                    setMessage('Failed to load settings.');
                }
            }

            setSelectedFile(null); // Clear any selected file on fresh fetch
            setImagePreview(null); // Clear any image preview on fresh fetch

        } catch (error) {
            console.error("General error in fetchSettings:", error);
        }
    }, []); // Removed setSettings from dependencies as it's not needed with fetchedSettings variable

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setSelectedFile(null);
            setImagePreview(null);
        }
        setShowChangeQrButton(false); // Hide button after file selection attempt
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let updatedQrCodeUrl = settings.qrCode;

            if (selectedFile) {
                const formData = new FormData();
                formData.append('qrCodeImage', selectedFile);

                try {
                    const response = await api.post('/api/settings/upload-qrcode', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });
                    updatedQrCodeUrl = response.data.qrCodeUrl;
                    setMessage('QR Code image uploaded successfully (pending Save Settings)!');
                } catch (error) {
                    console.error("Error uploading QR Code image:", error);
                    setMessage('Failed to upload QR Code image.');
                    return; 
                }
            }

            await updateAdminSettings({
                accountNumber: settings.accountNumber,
                bankName: settings.bankName,
                ifscCode: settings.ifscCode,
                upiId: settings.upiId,
                currentPayment: settings.currentPayment,
                qrCode: updatedQrCodeUrl,
            });
            setMessage('Settings updated successfully!');
            setSelectedFile(null);
            setImagePreview(null);
            await fetchSettings(); // Re-fetch settings to ensure UI is up-to-date and reset initialSettings
        } catch (error) {
            console.error("Error updating admin settings:", error);
            setMessage('Failed to update settings.');
        }
    };

    const handleCancel = async () => {
        setSettings(initialSettings); // Revert to initial settings
        setSelectedFile(null); // Clear any selected file
        setImagePreview(null); // Clear any image preview
        setShowChangeQrButton(false); // Hide the change QR button
        setMessage('Changes discarded.'); // Inform the user
    };

    return (
        <div className={`${styles.settingsPage} ${styles[theme]}`}>
            <h2 className={styles.pageTitle}>{settings.adminName}</h2>
            <p className={styles.adminIdDisplay}>Admin ID: {adminId}</p>
            <form onSubmit={handleSubmit} className={styles.settingsForm}>
                <div className={styles.formSection}>
                    <h3>Payment Details</h3>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label>A/C Number :</label>
                            <input type="text" name="accountNumber" value={settings.accountNumber} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Bank Name :</label>
                            <input type="text" name="bankName" value={settings.bankName} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>IFSC Code :</label>
                            <input type="text" name="ifscCode" value={settings.ifscCode} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>UPI ID :</label>
                            <input type="text" name="upiId" value={settings.upiId} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Payment Method :</label>
                            <select name="currentPayment" value={settings.currentPayment} onChange={handleChange}>
                                <option value="Net Banking">Net Banking</option>
                                <option value="UPI Id">UPI Id</option>
                                <option value="Both">Both</option>
                            </select>
                        </div>
                        <div className={`${styles.formGroup} ${styles.qrRow}`}>
                            <label>QR Code:</label>
                            <div>
                                {showChangeQrButton && (
                                    <div className={styles.qrButtonContainer}>
                                        <button
                                            type="button"
                                            className={styles.qrCodeChangeButton}
                                            onClick={() => { fileInputRef.current.click(); setShowChangeQrButton(false); }}
                                        >
                                            Change QR Code
                                        </button>
                                    </div>
                                )}
                                <div className={styles.qrCodeClickableArea} onClick={() => setShowChangeQrButton(prev => !prev)}>
                                    {settings.qrCode && !imagePreview && (
                                        <img 
                                            src={settings.qrCode} 
                                            alt="Current QR Code" 
                                            className={styles.qrCodeImage} 
                                            onError={(e) => { 
                                                e.target.style.display = 'none';
                                                const placeholder = document.createElement('div');
                                                placeholder.className = styles.qrCodePlaceholder;
                                                placeholder.innerText = 'Image broken or deleted. Click to re-upload.';
                                                e.target.parentElement.appendChild(placeholder);
                                            }}
                                        />
                                    )}
                                    {imagePreview && (
                                        <img src={imagePreview} alt="New QR Code Preview" className={styles.qrCodeImage} />
                                    )}
                                    {(!settings.qrCode || (settings.qrCode && !imagePreview && document.querySelector('.' + styles.qrCodePlaceholder))) && !imagePreview && (
                                        <div></div>
                                    )}
                                </div>
                            </div>
                            <input
                                type="file"
                                accept="image/jpeg,image/png"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>
                </div>

                {isDirty && (
                    <div className={styles.formActions}>
                        <button type="submit" className={styles.submitButton}>Save Settings</button>
                        <button type="button" className={styles.cancelButton} onClick={handleCancel}>Discard Changes</button>
                    </div>
                )}
            </form>
            {message && <p className={styles.successMessage}>{message}</p>}
        </div>
    );
};

export default SettingsPage;